import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { apiCall } from '@/utils/apiAxios';
import { Executive, AgentsOptions, SellersAgentOptions } from '@/types/agent/agent';
import { useRouter } from 'vue-router';
import { img } from '@/utils/img';
import { set } from 'lodash';

export const useAgentStore = defineStore('agent', () => {
    const agentsSelected = ref<AgentsOptions | null>(null);
    const agentsOptions = ref<AgentsOptions[]>([]);
    const executive = ref<Executive | null>(null);
    const sellersOptions = ref<SellersAgentOptions[] | null>(null);
    const isAgent = ref(localStorage.getItem('isAgent') === 'true');
    const agentHash = ref('');
    const groupsAgent = ref([]);
    const folderId = ref('');

    const userHasSellers = computed(() => {
        if (!sellersOptions.value) return false;
        return sellersOptions.value.length > 0;
    });

    const agentFullName = computed(() => {
        return `${agentsSelected.value?.first_name || ''} ${agentsSelected.value?.last_name || ''}`.trim();
    });

    const agentImg = computed(() => {
        if (agentsSelected.value?.image_url) {
            return img(agentsSelected.value.image_url); // Retorna la URL de la imagen si existe.
        }

        // Si no hay `image_url`, toma las iniciales.
        const firstnameInitial = agentsSelected.value?.first_name.charAt(0) || 'N';
        const lastnameInitial = agentsSelected.value?.last_name.charAt(0) || 'A';

        return `https://dummyimage.com/128x128/b8b8b8/ffffff.jpg&text=+${firstnameInitial}+${lastnameInitial}`; // Combina las iniciales y las convierte a mayúsculas.
    });

    const userHasMultipleSellers = computed(() => {
        if (!sellersOptions.value) return false;
        return sellersOptions.value.length > 1;
    });

    const router = useRouter();

    const resetAllVariables = () => {
        agentsSelected.value = null;
        agentsOptions.value = [];
        executive.value = null;
        isAgent.value = false;
        agentHash.value = '';
        groupsAgent.value = [];
    };

    const getInfoAgent = async () => {
        // Leer clave temporal de la URL si existe
        const params = new URLSearchParams(window.location.search);
        const tempKey = params.get('tempKey');

        if (tempKey) {
            const agentHashTemp = localStorage.getItem(tempKey);
            const impersonateLevel = localStorage.getItem(`${tempKey}_level`);

            if (agentHashTemp) {
                sessionStorage.setItem('agentHash', agentHashTemp);
                localStorage.removeItem(tempKey);
            }

            if (impersonateLevel) {
                sessionStorage.setItem('impersonateLevel', impersonateLevel);
                localStorage.removeItem(`${tempKey}_level`);
            }
        }

        const storedAgentsOptions = localStorage.getItem('agentsOptions');
        if (storedAgentsOptions) {
            agentsOptions.value = JSON.parse(storedAgentsOptions);
            const agentSelelected = agentsOptions.value.find((agent) => agent.hash === sessionStorage.getItem('agentHash'));
            if (agentSelelected) {
                setAgent(agentSelelected);
            } else {
                setAgent(agentsOptions.value[0]);
            }
        } else {
            await getInfoAgentGet();
        }
    };
    

    const getInfoAgentGet = async () => {
        const data = {};

        const { request } = await apiCall({
            method: 'get',
            url: `/brokerin/api/users/get_agents`,
            data: {},
            params: data
        });

        const response = await request;
        if (response.status == 1) {
            agentsOptions.value = response.response.agents;
            isAgent.value = response.response.is_agent;

            localStorage.setItem('agentsOptions', JSON.stringify(agentsOptions.value));
            localStorage.setItem('isAgent', response.response.is_agent);

            const agentHash = sessionStorage.getItem('agentHash');
            if (agentHash) {
                setAgentHash(agentHash);
            }else{
                setAgent(agentsOptions.value[0]);
            }
            
        }
    };

    const getSellersAgent = async () => {
        const storedSellersOptions = sessionStorage.getItem('sellersOptions');
        if (storedSellersOptions) {
            sellersOptions.value = JSON.parse(storedSellersOptions);
            return;
        }

        getSellersAgentApi();
    };

    const getSellersAgentApi = async () => {
        const data = {
            agent_hash: agentHash.value
        };

        const { request } = await apiCall({
            method: 'post',
            url: `/brokerin/api/users/get_sellers_agent`,
            data: data
        });

        const response = await request;
        if (response.status == 1) {
            sellersOptions.value = response.response.sellers;

            sessionStorage.setItem('sellersOptions', JSON.stringify(sellersOptions.value));
        } 
    };

    const selectMainAgent = (agentHash: string) => {
        const tempKey = `agentHashTemp_${Date.now()}`; // clave única por pestaña
        localStorage.setItem(tempKey, agentHash);
        const level = isAgent.value ? '2' : '0';
        localStorage.setItem(`${tempKey}_level`, level);

        // Abrir nueva pestaña con clave temporal en la URL
        window.open(`/private/dashboard/main?tempKey=${tempKey}`, '_blank');
    };
    

    const setAgent = (agent: any) => {
        agentsSelected.value = agent;
        setAgentHash(agent.hash);
    };

    const setAgentHash = async (agentHashValue: string) => {
        agentHash.value = agentHashValue;
        sessionStorage.setItem('agentHash', agentHashValue);
        await getInfoExecutive();
        getSellersAgentApi();
    };

    const getInfoExecutive = async () => {
        const sessionStorageExecutive = sessionStorage.getItem('executive') as string | null;
        if (sessionStorageExecutive !== null) {
            executive.value = JSON.parse(sessionStorageExecutive) as Executive;

            return;
        }

        const data = {
            agent_hash: agentHash.value
        };

        const { request } = await apiCall({
            method: 'get',
            url: `/brokerin/api/users/get_executive`,
            data: {},
            params: data
        });

        const response = await request;
        if (response.status == 1) {
            executive.value = response.response.executive;
            sessionStorage.setItem('executive', JSON.stringify(executive.value));
        } else {
            router.push({
                name: 'noExecutive'
            });
        }

        return;
    };

    return {
        agentsSelected,
        agentImg,
        agentsOptions,
        executive,
        isAgent,
        agentHash,
        groupsAgent,
        sellersOptions,
        userHasSellers,
        userHasMultipleSellers,
        agentFullName,
        folderId,
        resetAllVariables,
        getInfoAgent,
        getInfoAgentGet,
        selectMainAgent,
        getInfoExecutive,
        getSellersAgent
    };
});
